import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Check, Square, CheckSquare, UserCircle, MoreHorizontal, Copy, Trash2, ArrowRight, Calendar, UserPlus, Star, Clock } from 'lucide-react';
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from '../../../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Card, CardContent } from "../../../components/ui/card";
import { cn } from "../../../lib/utils";
import type { Todo } from './types';
import { useAuth } from '../../../contexts/AuthContext';
import apiFetch from '../../../lib/supabaseClient';
import { Database } from '../../../types/database';

type FilterCategory = 'active' | 'today' | 'whenever' | 'overdue' | 'done';
type Task = Database['public']['Tables']['tasks']['Row'];

const mapTaskToTodo = (task: Task): Todo => ({
  id: task.id,
  text: task.title,
  completed: task.status === 'completed',
  assignedTo: task.assigned_to || '',
  createdAt: task.created_at || new Date().toISOString(),
  starred: task.priority === 'high',
  isLowPriority: task.priority === 'low',
  dueDate: task.due_date || undefined,
});

const TodoList: React.FC = () => {
  const { user } = useAuth();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [newTodo, setNewTodo] = useState('');
  const [newTodoAssignee, setNewTodoAssignee] = useState('');
  const [newTodoDueDate, setNewTodoDueDate] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [editingTodoId, setEditingTodoId] = useState<string | null>(null);
  const [assigningTodoId, setAssigningTodoId] = useState<string | null>(null);
  const [assignInput, setAssignInput] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('active');
  const [showCompleted, setShowCompleted] = useState(false);

  const sortTodos = useCallback((list: Todo[]) => {
    const priority = (t: Todo) => {
      if (t.completed) return 3;
      if (t.starred) return 0; // do today at very top
      if (t.isLowPriority) return 2; // whenever items at bottom of active list
      return 1; // regular active todo
    };
    return [...list].sort((a, b) => priority(a) - priority(b));
  }, []);

  const fetchTodos = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiFetch(`/tasks?user_id=${user.id}&category=todo`);
      if (data) {
        const fetchedTodos = data.map(mapTaskToTodo);
        setTodos(sortTodos(fetchedTodos));
      }
    } catch (error) {
      console.error('Error fetching todos:', error);
    }
  }, [user, sortTodos]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);


  const isOlderThan30Days = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 30;
  };

  const isOverdue = (todo: Todo) => {
    if (todo.completed) return false;
    if (todo.dueDate) {
      const due = new Date(todo.dueDate);
      due.setHours(0,0,0,0);
      const today = new Date();
      today.setHours(0,0,0,0);
      return due < today;
    }
    return isOlderThan30Days(todo.createdAt);
  };

  const todoSummary = {
    all: todos.length,
    active: todos.filter(todo => !todo.completed).length,
    today: todos.filter(todo => todo.starred && !todo.completed).length,
    whenever: todos.filter(todo => !todo.completed && todo.isLowPriority).length,
    overdue: todos.filter(todo => isOverdue(todo) && !todo.isLowPriority).length,
    done: todos.filter(todo => todo.completed).length
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        if ((event.target as HTMLElement).tagName === 'INPUT') {
          return;
        }
        setActiveMenu(null);
        setAssigningTodoId(null);
      }

      if (editingTodoId && !(event.target as HTMLElement).closest('.todo-card')) {
        setEditingTodoId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMenu, editingTodoId]);

  const handleNewTodoKeyPress = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTodo.trim() && user) {
      const newRecord = {
        title: newTodo,
        user_id: user.id,
        assigned_to: newTodoAssignee || null,
        due_date: newTodoDueDate || null,
        category: 'todo' as const,
        status: 'todo' as const,
        priority: 'medium' as const,
      };

      try {
        const data = await apiFetch('/tasks', { method: 'POST', body: newRecord });
        if (data) {
          setTodos((prev) => [mapTaskToTodo(data), ...prev]);
        }
      } catch (error) {
        console.error("Error creating todo:", error);
      } finally {
        setNewTodo('');
        setNewTodoAssignee('');
        setNewTodoDueDate('');
        setIsCreating(false);
      }
    }
  };

  const toggleTodo = async (id: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;

    const newStatus = todo.completed ? 'todo' : 'completed';
    
    try {
      await apiFetch(`/tasks/${id}`, { method: 'PATCH', body: { status: newStatus } });
      fetchTodos();
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
    setActiveMenu(null);
  };

  const deleteTodo = async (id: string) => {
    try {
      await apiFetch(`/tasks/${id}`, { method: 'DELETE' });
      fetchTodos();
    } catch(error) {
      console.error("Error deleting todo:", error);
    }
    setActiveMenu(null);
    setEditingTodoId(null);
  };

  const duplicateTodo = async (todo: Todo) => {
    if (!user) return;
    const { text, assignedTo, dueDate } = todo;
    const newRecord = {
      title: text,
      user_id: user.id,
      assigned_to: assignedTo || null,
      due_date: dueDate || null,
      category: 'todo' as const,
      status: 'todo' as const,
      priority: 'medium' as const,
    };
    try {
        await apiFetch('/tasks', { method: 'POST', body: newRecord });
        fetchTodos();
    } catch (error) {
      console.error('Error duplicating todo:', error);
    }
    setActiveMenu(null);
  };

  const moveToTask = (id: string) => {
    console.log('Moving todo to task:', id);
    setActiveMenu(null);
  };

  const handleAssigneeChange = async (todoId: string, newAssignee: string) => {
    try {
      await apiFetch(`/tasks/${todoId}`, { method: 'PATCH', body: { assigned_to: newAssignee } });
      fetchTodos();
    } catch (error) {
      console.error('Error assigning todo:', error);
    }
    setActiveMenu(null);
  };

  const updateTodoText = async (id: string, newText: string) => {
    try {
      await apiFetch(`/tasks/${id}`, { method: 'PATCH', body: { title: newText } });
    } catch (error) {
      console.error('Error updating todo text:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  const handleTodoClick = (id: string) => {
    setEditingTodoId(editingTodoId === id ? null : id);
    setActiveMenu(null);
  };

  const toggleStarred = async (id: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;
    const newPriority = todo.starred ? 'medium' : 'high';

    try {
      await apiFetch(`/tasks/${id}`, { method: 'PATCH', body: { priority: newPriority } });
      fetchTodos();
    } catch (error) {
      console.error('Error starring todo:', error);
    }
    setActiveMenu(null);
  };

  const handleAssignClick = (id: string) => {
    setAssigningTodoId(id);
    setAssignInput('');
  };

  const handleAssignKeyPress = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' && assignInput.trim()) {
      handleAssigneeChange(id, assignInput.trim());
      setAssigningTodoId(null);
      setActiveMenu(null);
    } else if (e.key === 'Escape') {
      setAssigningTodoId(null);
      setActiveMenu(null);
    }
  };

  const getFilteredTodos = () => {
    if (activeFilter === 'done' && showCompleted) {
      return todos.filter(todo => todo.completed);
    }

    const filterMap = {
      'active': (todo: Todo) => !todo.completed,
      'today': (todo: Todo) => !todo.completed && todo.starred,
      'whenever': (todo: Todo) => !todo.completed && todo.isLowPriority,
      'overdue': (todo: Todo) => isOverdue(todo) && !todo.isLowPriority,
      'done': (todo: Todo) => false
    };

    return todos.filter(filterMap[activeFilter]);
  };

  const toggleLowPriority = async (id: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;
    const newPriority = todo.isLowPriority ? 'medium' : 'low';
    try {
      await apiFetch(`/tasks/${id}`, { method: 'PATCH', body: { priority: newPriority } });
      fetchTodos();
    } catch (error) {
      console.error('Error setting low priority:', error);
    }
    setActiveMenu(null);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-5 gap-2 mb-6">
        <button
          onClick={() => setActiveFilter('active')}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-green-500/10 hover:bg-green-500/20",
            activeFilter === 'active'
              ? "border-green-500"
              : "border-green-500/20 hover:border-green-500/40"
          )}
        >
          <span className="text-2xl font-semibold">{todoSummary.active}</span>
          <span className="text-sm text-muted-foreground">Active</span>
        </button>

        <button
          onClick={() => setActiveFilter('today')}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-warning/10 hover:bg-warning/20",
            activeFilter === 'today'
              ? "border-warning"
              : "border-warning/20 hover:border-warning/40"
          )}
        >
          <span className="text-2xl font-semibold">{todoSummary.today}</span>
          <span className="text-sm text-muted-foreground">Do Today</span>
        </button>

        <button
          onClick={() => setActiveFilter('whenever')}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-blue-500/10 hover:bg-blue-500/20",
            activeFilter === 'whenever'
              ? "border-blue-500"
              : "border-blue-500/20 hover:border-blue-500/40"
          )}
        >
          <span className="text-2xl font-semibold">{todoSummary.whenever}</span>
          <span className="text-sm text-muted-foreground">Whenever</span>
        </button>

        <button
          onClick={() => setActiveFilter('overdue')}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-destructive/10 hover:bg-destructive/20",
            activeFilter === 'overdue'
              ? "border-destructive"
              : "border-destructive/20 hover:border-destructive/40"
          )}
        >
          <span className="text-2xl font-semibold">{todoSummary.overdue}</span>
          <span className="text-sm text-muted-foreground">Overdue</span>
        </button>

        <button
          onClick={() => {
            setActiveFilter('done');
            setShowCompleted(prev => !prev);
          }}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-muted/30 hover:bg-muted/50",
            activeFilter === 'done' && showCompleted
              ? "border-slate-500"
              : "border-muted hover:border-slate-400"
          )}
        >
          <span className="text-2xl font-semibold">{showCompleted ? todoSummary.done : '—'}</span>
          <span className="text-sm text-muted-foreground">Done</span>
        </button>
      </div>

      <Card className={cn(
        "todo-card mb-4",
        isCreating && "ring-2 ring-primary ring-offset-2"
      )}>
        <CardContent className="p-2">
          <div className="flex items-start gap-3">
            <Square className="w-5 h-5 mt-1 text-muted-foreground" />
            <div className="flex-1 flex flex-col gap-1">
              <Input
                type="text"
                placeholder="What do you need to do?"
                value={newTodo}
                onChange={(e) => {
                  setNewTodo(e.target.value);
                  if (!isCreating && e.target.value) setIsCreating(true);
                }}
                onKeyPress={handleNewTodoKeyPress}
                className="border-0 p-0 h-auto text-sm focus-visible:ring-0"
              />
              {isCreating && (
                <>
                  <div className="flex items-center gap-2 bg-muted/50 rounded-md p-1">
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="Assign to (email)"
                      value={newTodoAssignee}
                      onChange={(e) => setNewTodoAssignee(e.target.value)}
                      onKeyPress={handleNewTodoKeyPress}
                      className="border-0 bg-transparent text-sm h-auto p-0 focus-visible:ring-0"
                    />
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={newTodoDueDate}
                      onChange={(e) => setNewTodoDueDate(e.target.value)}
                      onKeyPress={handleNewTodoKeyPress}
                      className="border-0 bg-transparent text-xs h-auto p-0 focus-visible:ring-0 w-[110px]"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-1">
        {getFilteredTodos().map(todo => (
          <Card
            key={todo.id}
            className={cn(
              "todo-card transition-all cursor-pointer",
              editingTodoId !== todo.id && [
                todo.completed && "border-primary/30 dark:bg-muted/30",
                todo.starred && "bg-warning/5 dark:bg-warning/20 border-warning",
                !todo.completed && isOverdue(todo) && !todo.isLowPriority && "bg-destructive/5 dark:bg-destructive/20 border-destructive/30",
                !todo.completed && !todo.starred && !isOverdue(todo) && !todo.isLowPriority && "dark:bg-slate-800/60 border",
                todo.isLowPriority && !todo.completed && "bg-blue-500/5 dark:bg-blue-500/10 border-blue-500/30",
                !todo.completed && !todo.starred && !isOverdue(todo) && !todo.isLowPriority && !todo.completed && !todo.isLowPriority && "border"
              ],
              todo.starred
                ? "hover:ring-1 hover:ring-warning"
                : todo.isLowPriority && !todo.completed
                  ? "hover:ring-1 hover:ring-blue-500"
                  : "hover:ring-1 hover:ring-primary",
              editingTodoId === todo.id && 
                (todo.starred 
                  ? "ring-2 ring-warning ring-offset-1 bg-warning/10"
                  : "ring-2 ring-primary ring-offset-1 bg-muted/50")
            )}
          >
            <CardContent className={cn(
              "p-1 transition-all duration-200",
              editingTodoId === todo.id && "pb-2"
            )}>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "p-0 w-6 h-6 hover:bg-muted shrink-0",
                    todo.completed && "text-blue-600",
                    todo.starred && !todo.completed && "text-warning"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTodo(todo.id);
                  }}
                >
                  {todo.completed ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : todo.starred ? (
                    <Star className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </Button>

                <div className="flex-1 min-w-0 flex items-center gap-1">
                  <div className="flex-1 min-w-0">
                    {editingTodoId === todo.id ? (
                      <Textarea
                        value={todo.text}
                        onChange={(e) => updateTodoText(todo.id, e.target.value)}
                        className="bg-transparent px-2 py-1 text-sm resize-none h-auto min-h-[36px] focus-visible:outline-none border-0"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            setEditingTodoId(null);
                          } else if (e.key === 'Escape') {
                            setEditingTodoId(null);
                          }
                        }}
                      />
                    ) : (
                      <span
                        className={cn(
                          "text-sm block truncate px-2 rounded hover:bg-muted/50",
                          todo.completed && "line-through text-muted-foreground"
                        )}
                        onClick={() => handleTodoClick(todo.id)}
                      >
                        {todo.text}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {todo.assignedTo && (
                      <span className="flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-600/20 px-2 py-0.5 rounded-full text-blue-600 dark:text-blue-300 whitespace-nowrap">
                        <UserCircle className="w-3 h-3" />
                        {todo.assignedTo}
                      </span>
                    )}
                    
                    {todo.dueDate ? (
                      <span className={cn(
                        "flex items-center gap-1 text-xs whitespace-nowrap",
                        isOverdue(todo) && "text-destructive"
                      )}>
                        <Calendar className="w-3 h-3" />
                        {formatDate(todo.dueDate)}
                      </span>
                    ) : (
                      <span className={cn(
                        "text-xs text-muted-foreground whitespace-nowrap",
                        isOverdue(todo) && "text-destructive"
                      )}>
                        {formatDate(todo.createdAt)}
                      </span>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-0 w-6 h-6 hover:bg-muted"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className={cn(
                        "w-48"
                      )}>
                        <DropdownMenuItem onClick={() => toggleStarred(todo.id)}>
                          <Star
                            className={cn(
                              "w-4 h-4 mr-2",
                              todo.starred ? "text-warning" : ""
                            )}
                            fill={todo.starred ? "currentColor" : "none"}
                          />
                          {todo.starred ? 'Remove from Today' : 'Do Today'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleLowPriority(todo.id)}>
                          <Clock
                            className={cn(
                              "w-4 h-4 mr-2",
                              todo.isLowPriority ? "text-blue-500" : ""
                            )}
                          />
                          {todo.isLowPriority ? 'Make Normal Priority' : 'Do Whenever'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            handleAssignClick(todo.id);
                          }}
                          className={cn(assigningTodoId === todo.id && 'bg-muted')}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Assign
                        </DropdownMenuItem>
                        {assigningTodoId === todo.id && (
                          <div className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                            <Input
                              type="email"
                              placeholder="Enter email to assign"
                              value={assignInput}
                              onChange={(e) => setAssignInput(e.target.value)}
                              onKeyDown={(e) => handleAssignKeyPress(e, todo.id)}
                              autoFocus
                              className="w-full"
                            />
                          </div>
                        )}
                        <DropdownMenuItem onClick={() => moveToTask(todo.id)}>
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Move to Task
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicateTodo(todo)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => deleteTodo(todo.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(todo.createdAt)}
                        </DropdownMenuLabel>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TodoList; 